import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const ManageExpenseCategory = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true)
      setErrorMessage('')
      const response = await apiClient.get(API_ENDPOINTS.EXPENSE_CATEGORIES)
      const data = Array.isArray(response.data) ? response.data : response.data?.results || []
      setCategories(data)
    } catch (error) {
      console.error('Failed to load expense categories:', error)
      setErrorMessage(
        error.response?.data?.detail ||
          'Unable to load expense categories right now. Please try again later.'
      )
      setCategories([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  const filteredCategories = useMemo(() => {
    if (!searchTerm) {
      return categories
    }
    const query = searchTerm.toLowerCase()
    return categories.filter(
      (category) =>
        category.name?.toLowerCase().includes(query) ||
        category.description?.toLowerCase().includes(query)
    )
  }, [categories, searchTerm])

  const totalCategories = categories.length

  const handleEdit = async (category) => {
    // Escape HTML to prevent XSS
    const escapedName = (category.name || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
    const escapedDescription = (category.description || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/\n/g, '\\n')
    
    const { value: formValues } = await Swal.fire({
      title: 'Edit Expense Category',
      html: `
        <input id="swal-input1" class="swal2-input" placeholder="Category Name" value="${escapedName}" required>
        <textarea id="swal-input2" class="swal2-textarea" placeholder="Description (optional)" style="resize: vertical; min-height: 80px;">${escapedDescription}</textarea>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      preConfirm: () => {
        const name = document.getElementById('swal-input1').value.trim()
        const description = document.getElementById('swal-input2').value.trim()
        if (!name) {
          Swal.showValidationMessage('Category name is required')
          return false
        }
        return { name, description }
      },
      didOpen: () => {
        const input = document.getElementById('swal-input1')
        input.focus()
        input.select()
      }
    })

    if (formValues) {
      try {
        const payload = {
          name: formValues.name,
          description: formValues.description || ''
        }

        await apiClient.put(`${API_ENDPOINTS.EXPENSE_CATEGORIES}${category.id}/`, payload)

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Expense category updated successfully!'
        })

        fetchCategories() // Refresh the list
      } catch (error) {
        console.error('Error updating expense category:', error)
        const errorMessage = error.response?.data?.name?.[0] || 
                            error.response?.data?.description?.[0] ||
                            error.response?.data?.non_field_errors?.[0] ||
                            error.response?.data?.detail ||
                            error.response?.data?.message ||
                            'Failed to update expense category. Please try again.'
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage
        })
      }
    }
  }

  const handleDelete = async (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId)
    
    // Escape HTML to prevent XSS
    const escapedName = category?.name?.replace(/"/g, '&quot;').replace(/'/g, '&#39;') || ''
    
    const { value: typedName } = await Swal.fire({
      title: 'Delete Expense Category',
      html: `
        <p>Are you sure you want to delete this expense category?</p>
        <p class="text-danger"><strong>This action cannot be undone!</strong></p>
        <p>Type <strong>"${escapedName}"</strong> to confirm:</p>
        <input id="swal-input" class="swal2-input" placeholder="Type category name">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
      allowEscapeKey: true,
      didOpen: () => {
        const input = document.getElementById('swal-input')
        const confirmButton = Swal.getConfirmButton()
        
        // Initially disable the confirm button
        confirmButton.disabled = true
        confirmButton.style.opacity = '0.5'
        confirmButton.style.cursor = 'not-allowed'
        
        // Add input event listener to enable/disable button based on match
        input.addEventListener('input', () => {
          const inputValue = input.value.trim()
          const matches = inputValue === category?.name
          
          if (matches) {
            confirmButton.disabled = false
            confirmButton.style.opacity = '1'
            confirmButton.style.cursor = 'pointer'
          } else {
            confirmButton.disabled = true
            confirmButton.style.opacity = '0.5'
            confirmButton.style.cursor = 'not-allowed'
          }
        })
        
        input.focus()
      },
      preConfirm: () => {
        const input = document.getElementById('swal-input')
        const inputValue = input.value.trim()
        
        if (!inputValue) {
          Swal.showValidationMessage('You need to type the category name!')
          return false
        }
        
        if (inputValue !== category?.name) {
          Swal.showValidationMessage('The name does not match!')
          return false
        }
        
        return inputValue
      }
    })

    if (typedName === category?.name) {
      try {
        await apiClient.delete(`${API_ENDPOINTS.EXPENSE_CATEGORIES}${categoryId}/`)
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Expense category has been deleted.',
          timer: 2000,
          showConfirmButton: false
        })
        fetchCategories() // Refresh the list
      } catch (error) {
        console.error('Error deleting expense category:', error)
        const errorMessage = error.response?.data?.detail ||
                            error.response?.data?.message ||
                            'Failed to delete expense category. Please try again.'
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage
        })
      }
    }
  }

  const renderTable = () => {
    if (loading) {
      return (
        <div className="py-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )
    }

    if (!filteredCategories.length) {
      return (
        <div className="py-5 text-center text-muted">
          <i className="bi bi-tags fs-1 d-block mb-3" />
          <p className="mb-0">
            {searchTerm ? 'No categories match your search.' : 'No categories have been created yet.'}
          </p>
        </div>
      )
    }

    return (
      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Description</th>
              <th scope="col" className="text-end">Created</th>
              <th scope="col" className="text-end" style={{ width: '150px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategories.map((category) => (
              <tr key={category.id}>
                <td className="fw-semibold">{category.name}</td>
                <td className="text-muted">{category.description || '—'}</td>
                <td className="text-end text-muted">
                  {category.created_at ? new Date(category.created_at).toLocaleDateString() : '—'}
                </td>
                <td className="text-end">
                  <div className="btn-group" role="group">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleEdit(category)}
                      title="Edit category"
                    >
                      <i className="bi bi-pencil-fill"></i>
                      <span className="ms-1 d-none d-md-inline">Edit</span>
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(category.id)}
                      title="Delete category"
                    >
                      <i className="bi bi-trash-fill"></i>
                      <span className="ms-1 d-none d-md-inline">Delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Manage Expense Categories</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#">Home</a></li>
            <li className="breadcrumb-item">Expenses</li>
            <li className="breadcrumb-item active">Manage Expense Categories</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-12">
            {errorMessage && (
              <div className="alert alert-danger" role="alert">
                {errorMessage}
              </div>
            )}

            <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-3">
              <div
                className="p-3 rounded-3 text-white"
                style={{
                  background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
                  boxShadow: '0 6px 18px rgba(0, 114, 255, 0.25)'
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 48, height: 48, backgroundColor: 'rgba(255,255,255,0.2)' }}
                  >
                    <i className="bi bi-tags fs-4" />
                  </div>
                  <div>
                    <small className="text-uppercase fw-semibold text-white-50">Total Categories</small>
                    <h3 className="mb-0">{totalCategories}</h3>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search categories"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  style={{ minWidth: 220 }}
                />
                <button type="button" className="btn btn-outline-primary" onClick={fetchCategories}>
                  <i className="bi bi-arrow-repeat me-1" />
                  Refresh
                </button>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between flex-wrap align-items-center gap-2 mb-3">
                  <div>
                    <h5 className="card-title mb-0">Expense Categories</h5>
                    <small className="text-muted">Manage expense categories used across the system.</small>
                  </div>
                </div>
                {renderTable()}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ManageExpenseCategory

